import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSignedUrl } from '../../hooks/useSignedUrl';

/**
 * Image component with lazy URL signing.
 * Shows loading skeleton while signed URL is being fetched.
 */
const SignedImage = ({ url, alt, className, style, draggable }) => {
  const { signedUrl, loading } = useSignedUrl(url);

  if (!signedUrl) {
    // Loading skeleton
    return (
      <div
        className={`bg-gray-700 animate-pulse rounded-lg ${className}`}
        style={{ ...style, minHeight: '200px', minWidth: '200px' }}
      />
    );
  }

  return (
    <img
      src={signedUrl}
      alt={alt}
      className={className}
      style={style}
      draggable={draggable}
    />
  );
};

export const ImageModal = ({ images, currentIndex, onClose, onIndexChange, onOrder }) => {
  // Guard: Handle edge case where images array is empty
  // This can happen when a job is canceled early (e.g., at 0% progress) with no results
  // Instead of crashing, close the modal gracefully
  if (!images || images.length === 0) {
    console.warn('[ImageModal] No images to display, closing modal');
    // Close modal immediately - can't use useEffect here due to hooks rules
    onClose();
    return null;
  }

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [scale, setScale] = useState(1);

  const currentImage = images[currentIndex];
  const hasMultipleImages = images.length > 1;

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentIndex < images.length - 1) {
      handleNext();
    }
    if (isRightSwipe && currentIndex > 0) {
      handlePrevious();
    }
  };

  const handleNext = () => {
    if (currentIndex < images.length - 1) {
      onIndexChange(currentIndex + 1);
      setScale(1); // Reset zoom
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      onIndexChange(currentIndex - 1);
      setScale(1); // Reset zoom
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') handlePrevious();
    if (e.key === 'ArrowRight') handleNext();
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden'; // Prevent background scroll

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [currentIndex]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={handleBackdropClick}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-3 bg-white/10 hover:bg-white/20
          rounded-full transition-colors backdrop-blur-sm"
        title="Close"
      >
        <X className="w-8 h-8 text-white" />
      </button>

      {/* Image Title */}
      <div className="absolute top-4 left-4 z-50 bg-black/50 px-6 py-3 rounded-2xl backdrop-blur-sm">
        <h3 className="text-white text-xl font-bold">
          {currentImage.label}
        </h3>
        {hasMultipleImages && (
          <p className="text-white/70 text-sm">
            {currentIndex + 1} of {images.length}
          </p>
        )}
      </div>

      {/* Previous Button */}
      {hasMultipleImages && currentIndex > 0 && (
        <button
          onClick={handlePrevious}
          className="absolute left-4 z-50 p-3 bg-white/10 hover:bg-white/20
            rounded-full transition-colors backdrop-blur-sm"
          title="Previous"
        >
          <ChevronLeft className="w-8 h-8 text-white" />
        </button>
      )}

      {/* Next Button */}
      {hasMultipleImages && currentIndex < images.length - 1 && (
        <button
          onClick={handleNext}
          className="absolute right-4 z-50 p-3 bg-white/10 hover:bg-white/20
            rounded-full transition-colors backdrop-blur-sm"
          title="Next"
        >
          <ChevronRight className="w-8 h-8 text-white" />
        </button>
      )}

      {/* Image Container */}
      <div
        className="relative w-full h-full flex items-center justify-center p-4"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <SignedImage
          url={currentImage.url}
          alt={currentImage.label}
          className="max-w-full max-h-[70vh] object-contain transition-transform duration-300"
          style={{ transform: `scale(${scale})` }}
          draggable={false}
        />
      </div>

      {/* Swipe Indicator (mobile) */}
      {hasMultipleImages && (
        <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2
          bg-black/50 px-4 py-2 rounded-full backdrop-blur-sm">
          <p className="text-white text-sm">
            ← Swipe to navigate →
          </p>
        </div>
      )}

      {/* Order Button (Sticky at Bottom) */}
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
        <button
          onClick={onOrder}
          className="w-full max-w-md mx-auto block h-16 text-xl font-bold text-white rounded-2xl shadow-2xl
            bg-gradient-to-r from-red-500 to-orange-500
            hover:from-red-600 hover:to-orange-600
            transform hover:scale-105 transition-all duration-300"
        >
          🛒 ORDER IT NOW!
        </button>
      </div>

      {/* Dots Indicator */}
      {hasMultipleImages && (
        <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 flex space-x-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => onIndexChange(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-white w-8'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              title={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
